import { css } from "@emotion/react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React from "react";

import BaseLink from "~/components/links/BaseLink";

import Icon from "~/components/IcoIcon";

import { link } from "~/styles/typographyStyles";

import transition from "~/utilities/transition";
import { sanitizeAndParseHTML } from "~/utilities/helpers";

const getWrapperStyles = ({ theme, vertical }) => {
    if (vertical) {
        return css``;
    }

    return css`
        display: grid;
        grid-template-columns: repeat(1, 1fr);

        @media (min-width: ${theme.breakpoints.tabletPortrait}) {
            grid-template-columns: repeat(2, 1fr);
        }

        @media (min-width: ${theme.breakpoints.tabletLandscape}) {
            grid-template-columns: repeat(3, 1fr);
        }
    `;
};

const getLinkBorderStyles = ({ theme, vertical }) => {
    if (vertical) {
        return css`
            border: ${`1px solid ${theme.palette.darkGray}`};

            &:last-of-type {
                border-top: 0;
            }
        `;
    }

    return css`
        @media (max-width: ${theme.breakpoints.tabletPortraitMax}) {
            border-bottom: ${`1px solid ${theme.palette.darkGray}`};
            border-left: ${`1px solid ${theme.palette.darkGray}`};
            border-right: ${`1px solid ${theme.palette.darkGray}`};

            &:first-of-type {
                border-top: ${`1px solid ${theme.palette.darkGray}`};
            }
        }

        @media (min-width: ${theme.breakpoints
                .tabletPortrait}) and (max-width: ${theme.breakpoints
                .tabletLandscapeMax}) {
            border-bottom: ${`1px solid ${theme.palette.darkGray}`};
            border-right: ${`1px solid ${theme.palette.darkGray}`};

            &:nth-of-type(-n + 2) {
                border-top: ${`1px solid ${theme.palette.darkGray}`};
            }

            &:nth-of-type(2n + 1) {
                border-left: ${`1px solid ${theme.palette.darkGray}`};
            }
        }

        @media (min-width: ${theme.breakpoints.tabletLandscape}) {
            border-bottom: ${`1px solid ${theme.palette.darkGray}`};
            border-right: ${`1px solid ${theme.palette.darkGray}`};

            &:nth-of-type(-n + 3) {
                border-top: ${`1px solid ${theme.palette.darkGray}`};
            }

            &:nth-of-type(3n + 1) {
                border-left: ${`1px solid ${theme.palette.darkGray}`};
            }
        }
    `;
};

const Wrapper = styled.div`
    ${getWrapperStyles};
`;

const StyledIcon = styled(Icon)`
    margin-right: 12px;
`;

const Text = styled.span`
    ${link};
    color: ${({ backgroundColor, theme }) =>
        backgroundColor === "darkBlue"
            ? theme.palette.white
            : theme.palette.black};
`;

const backgroundHover = ({ theme, vertical, backgroundColor }) => {
    const hovers = {
        darkBlue: theme.palette.darkNavy,
        white: theme.palette.lightGray,
        lightGray: theme.palette.white,
    };

    return css`
        background-color: ${hovers.hasOwnProperty(backgroundColor)
            ? hovers[backgroundColor]
            : theme.palette.lightGray};
    `;
};

const Card = styled(BaseLink)`
    ${transition({}, "background-color")};
    ${getLinkBorderStyles};
    display: flex;
    align-items: center;
    width: 100%;
    padding: 20px 30px;

    ${StyledIcon} {
        flex-shrink: 0;
        flex-grow: 0;
    }

    ${Text} {
        flex-grow: 1;
    }

    &:hover,
    &:focus {
        ${backgroundHover};
    }
`;

const IconLink = ({
    iconLinks = [],
    backgroundColor = "white",
    vertical = false,
    tagLabel = null,
    tagRef = null,
}) => (
    <Wrapper {...{ vertical }}>
        {iconLinks.map(({ iconName, linkLabel, linkUrl }, index) => (
            <Card
                to={linkUrl}
                key={`${index}-${linkLabel}`}
                {...{ backgroundColor, vertical, tagLabel, tagRef }}
            >
                <StyledIcon icon={iconName} size={40} color="lightBlue" />
                <Text {...{ backgroundColor }}>
                    {sanitizeAndParseHTML(linkLabel)}
                </Text>
            </Card>
        ))}
    </Wrapper>
);

IconLink.propTypes = {
    backgroundColor: PropTypes.oneOf(["white", "darkBlue", "lightGray"]),
    iconLinks: PropTypes.arrayOf(
        PropTypes.shape({
            iconName: PropTypes.string,
            linkUrl: PropTypes.string.isRequired,
            linkLabel: PropTypes.string.isRequired,
        }),
    ).isRequired,
    tagLabel: PropTypes.string,
    tagRef: PropTypes.object,
    vertical: PropTypes.bool,
};

export default IconLink;
